/**
 * Iter 236 — Page de pointage salariés (mobile-first).
 *
 * Routes :
 *  - /punch                — pointage standard (auth requis)
 *  - /punch?t={token}&term={id}  — depuis un QR code de poste fixe
 *
 * Flow :
 *  1. Affiche état actuel (en cours / arrêté)
 *  2. Auto-géolocalisation (Geolocation API)
 *  3. Bouton "Démarrer / Arrêter mon pointage" → POST /tracking/punch/start
 *  4. Saisie OTP reçu par SMS → POST /tracking/punch/verify
 *  5. Confirmation + redirection
 *
 * Offline support : si fetch fail, push dans localStorage + retry au load suivant via /tracking/sync-offline.
 */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Clock, MapPin, Smartphone, CheckCircle2, AlertCircle, LogIn, LogOut, Wifi, WifiOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function PunchPage() {
  const { user, axiosAuth } = useAuth();
  const [search] = useSearchParams();
  const qrToken = search.get("t");
  const terminalId = search.get("term");

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("idle"); // idle | geolocating | sending_otp | enter_otp | success
  const [otpId, setOtpId] = useState(null);
  const [code, setCode] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [gps, setGps] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosAuth().get("/tracking/employees/me/today");
      setStatus(r.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const requestGps = async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
      );
    });
  };

  const initiate = async (mode) => {
    setError(null);
    setStep("geolocating");
    const pos = await requestGps();
    setGps(pos);
    setStep("sending_otp");
    try {
      const body = { mode, ...(pos || {}) };
      if (qrToken && terminalId) {
        body.qr_token = qrToken;
        body.terminal_id = terminalId;
      }
      const r = await axiosAuth().post("/tracking/punch/start", body);
      setOtpId(r.data.otp_id);
      setPhoneMasked(r.data.phone_masked);
      setStep("enter_otp");
      toast.success(`SMS envoyé au ${r.data.phone_masked}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setStep("idle");
      // Si offline → queue
      if (!navigator.onLine) {
        const queue = JSON.parse(localStorage.getItem("punch_offline_queue") || "[]");
        queue.push({ mode, lat: pos?.lat, lng: pos?.lng, timestamp: new Date().toISOString() });
        localStorage.setItem("punch_offline_queue", JSON.stringify(queue));
        toast("⚠️ Hors ligne. Pointage en attente de synchronisation.", { duration: 5000 });
      }
    }
  };

  const verifyOtp = async () => {
    if (!code || code.length !== 6) {
      setError("Code à 6 chiffres requis");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await axiosAuth().post("/tracking/punch/verify", { otp_id: otpId, code });
      setStep("success");
      toast.success(r.data.mode === "start" ? "Pointage démarré ✓" : `Pointage terminé · ${r.data.duration_minutes} min`);
      setCode(""); setOtpId(null);
      setTimeout(() => { setStep("idle"); reload(); }, 2000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Sync offline queue at mount
  useEffect(() => {
    const queue = JSON.parse(localStorage.getItem("punch_offline_queue") || "[]");
    if (queue.length > 0 && navigator.onLine) {
      axiosAuth().post("/tracking/sync-offline", { queue }).then((r) => {
        if (r.data.accepted > 0) {
          toast.success(`✓ ${r.data.accepted} pointage(s) hors ligne synchronisé(s)`);
        }
        localStorage.setItem("punch_offline_queue", "[]");
        reload();
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return (
    <div className="min-h-screen bg-[#14101c] flex items-center justify-center px-4">
      <div className="bg-[#0b0811] border border-amber-500/30 rounded-2xl p-8 max-w-md text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-3" />
        <h1 className="text-xl font-bold text-white mb-2">Connexion requise</h1>
        <p className="text-sm text-slate-400 mb-4">Connectez-vous d'abord pour pointer votre journée.</p>
        <a href="/login" className="inline-block px-6 py-2 bg-[#f7c935] text-slate-900 rounded-lg font-bold">Se connecter</a>
      </div>
    </div>
  );

  const openPunch = status?.open_punch;
  const todayHours = status?.today_total_hours || 0;

  return (
    <div className="min-h-screen bg-[#14101c] py-6 px-4" data-testid="punch-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto bg-cyan-500/20 border border-cyan-500/40 rounded-2xl flex items-center justify-center mb-4">
            <Clock className="w-7 h-7 text-cyan-300" />
          </div>
          <h1 className="text-2xl font-black text-white">Pointage salarié</h1>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className="text-sm text-slate-400">{user.name || user.email}</span>
            {online ? (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-400"><Wifi className="w-3 h-3" /> en ligne</span>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-400"><WifiOff className="w-3 h-3" /> hors ligne</span>
            )}
          </div>
          {qrToken && terminalId && (
            <div className="mt-2 inline-block bg-cyan-500/15 border border-cyan-500/30 rounded-lg px-3 py-1 text-[11px] text-cyan-300">
              📍 Scan QR poste fixe
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : step === "success" ? (
          <SuccessCard />
        ) : step === "enter_otp" ? (
          <OtpCard
            phoneMasked={phoneMasked}
            code={code}
            setCode={setCode}
            onSubmit={verifyOtp}
            submitting={submitting}
            error={error}
            onBack={() => { setStep("idle"); setError(null); setOtpId(null); }}
          />
        ) : openPunch ? (
          <ActivePunchCard
            punch={openPunch}
            todayHours={todayHours}
            onStop={() => initiate("stop")}
            loading={step !== "idle"}
            error={error}
          />
        ) : (
          <IdleCard
            todayHours={todayHours}
            onStart={() => initiate("start")}
            loading={step !== "idle"}
            error={error}
            gps={gps}
          />
        )}

        <p className="text-[10px] text-center text-slate-600 mt-6">
          Blitzz · pointage GPS sécurisé par SMS · données conformes RGPD
        </p>
      </div>
    </div>
  );
}


// ─── Cards ───────────────────────────────────────────


function IdleCard({ todayHours, onStart, loading, error, gps }) {
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-2xl p-6" data-testid="punch-idle-card">
      <div className="text-center mb-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Aujourd'hui</div>
        <div className="text-3xl font-black text-white">{todayHours.toFixed(1)} h</div>
      </div>
      <Button onClick={onStart} disabled={loading}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold h-14 text-base"
        data-testid="punch-start-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <LogIn className="w-5 h-5 mr-2" />}
        Démarrer mon pointage
      </Button>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-slate-400">
          <MapPin className="w-3.5 h-3.5" />
          {gps ? <span className="text-emerald-400">GPS OK</span> : <span>GPS auto</span>}
        </div>
        <div className="flex items-center gap-1.5 text-slate-400">
          <Smartphone className="w-3.5 h-3.5" /> SMS de confirmation
        </div>
      </div>
      {error && <ErrorMessage error={error} />}
    </div>
  );
}


function ActivePunchCard({ punch, todayHours, onStop, loading, error }) {
  const startTime = new Date(punch.start_at);
  const minutesElapsed = Math.floor((Date.now() - startTime.getTime()) / 60000);
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6" data-testid="punch-active-card">
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/20 rounded-full text-emerald-300 text-xs font-bold mb-3">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" /> En cours
        </div>
        <div className="text-3xl font-black text-white">{Math.floor(minutesElapsed / 60)}h {minutesElapsed % 60}min</div>
        <div className="text-[11px] text-slate-400 mt-1">Démarré à {startTime.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
        {punch.start_geofence_status === "outside" && (
          <div className="mt-2 inline-block bg-amber-500/15 border border-amber-500/30 rounded-lg px-3 py-1 text-[11px] text-amber-300">
            ⚠️ Pointage hors zone géofence
          </div>
        )}
      </div>
      <Button onClick={onStop} disabled={loading}
        className="w-full bg-rose-500 hover:bg-rose-400 text-white font-bold h-14 text-base"
        data-testid="punch-stop-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <LogOut className="w-5 h-5 mr-2" />}
        Arrêter mon pointage
      </Button>
      <div className="text-center text-xs text-slate-400 mt-3">Total aujourd'hui : <strong className="text-white">{todayHours.toFixed(1)}h</strong></div>
      {error && <ErrorMessage error={error} />}
    </div>
  );
}


function OtpCard({ phoneMasked, code, setCode, onSubmit, submitting, error, onBack }) {
  return (
    <div className="bg-[#0b0811] border border-cyan-500/30 rounded-2xl p-6" data-testid="punch-otp-card">
      <div className="text-center mb-5">
        <div className="w-12 h-12 mx-auto bg-cyan-500/20 border border-cyan-500/40 rounded-2xl flex items-center justify-center mb-3">
          <Smartphone className="w-6 h-6 text-cyan-300" />
        </div>
        <div className="text-base font-bold text-white">Code envoyé par SMS</div>
        <div className="text-xs text-slate-400 mt-1">au <strong className="text-cyan-300">{phoneMasked}</strong></div>
      </div>
      <Input
        type="tel" inputMode="numeric" maxLength={6} pattern="\\d{6}"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="• • • • • •"
        className="bg-[#14101c] border-white/10 text-white text-center text-3xl font-black tracking-[0.5em] h-16 mb-4"
        autoFocus
        data-testid="punch-otp-input"
      />
      <Button onClick={onSubmit} disabled={code.length !== 6 || submitting}
        className="w-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold h-12"
        data-testid="punch-otp-submit">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
        Valider
      </Button>
      <button onClick={onBack} className="w-full mt-2 text-xs text-slate-400 hover:text-slate-200">
        ← Annuler
      </button>
      {error && <ErrorMessage error={error} />}
    </div>
  );
}


function SuccessCard() {
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center" data-testid="punch-success">
      <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-3" />
      <div className="text-xl font-black text-white">Pointage enregistré</div>
      <div className="text-sm text-slate-400 mt-1">Bonne journée !</div>
    </div>
  );
}


function ErrorMessage({ error }) {
  return (
    <div className="mt-3 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-xs text-rose-300" data-testid="punch-error">
      <strong>Erreur :</strong> {error}
    </div>
  );
}
